import { router } from '@inertiajs/react'
import { useEffect, useState, useCallback, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'
import Layout from '@/components/layout/Layout'
import ChatPanel from '@/components/landing-pages/ChatPanel'
import ChatInput from '@/components/landing-pages/ChatInput'
import PreviewPanel from '@/components/landing-pages/PreviewPanel'
import { ArrowLeft, Upload, Pencil, Loader2, Copy, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface AskUserQuestion {
  question: string
  input_type: 'text' | 'buttons' | 'dropdown'
  options?: string[]
}

interface Message {
  id: string
  role: string
  content: string
  created_at: string
  message_type?: string
  metadata?: AskUserQuestion
  answered?: string
}

interface SessionData {
  id: string
  name: string
  slug: string
  product: string
  status: string
  draft_html: string | null
  published_url: string | null
  published_at: string | null
  processing: boolean
}

interface Props {
  session: SessionData
  messages: Message[]
}

interface CableData {
  type: 'text' | 'complete' | 'error' | 'published' | 'publish_error' | 'ask_user'
  content?: string
  draft_html?: string
  message?: string
  url?: string
  question?: AskUserQuestion
}

let cable: ReturnType<typeof createConsumer> | null = null
function getCable() {
  if (!cable) cable = createConsumer()
  return cable
}

export default function LandingPageShow({ session, messages: initialMessages }: Props) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [draftHtml, setDraftHtml] = useState<string | null>(session.draft_html)
  const [status, setStatus] = useState(session.status)
  const [publishedUrl, setPublishedUrl] = useState(session.published_url)
  const [streaming, setStreaming] = useState(session.processing || false)
  const [publishing, setPublishing] = useState(false)
  const streamingContentRef = useRef('')

  useEffect(() => {
    const subscription = getCable().subscriptions.create(
      { channel: 'LandingPageChannel', session_id: session.id },
      {
        received(data: CableData) {
          if (data.type === 'text') {
            streamingContentRef.current += data.content || ''
            setMessages(prev => {
              const last = prev[prev.length - 1]
              if (last && last.role === 'assistant' && last.id === 'streaming') {
                return [...prev.slice(0, -1), { ...last, content: streamingContentRef.current }]
              }
              return [...prev, {
                id: 'streaming',
                role: 'assistant',
                content: streamingContentRef.current,
                created_at: new Date().toISOString()
              }]
            })
            setStreaming(true)
          } else if (data.type === 'complete') {
            setStreaming(false)
            if (data.draft_html) {
              setDraftHtml(data.draft_html)
            }
            if (streamingContentRef.current) {
              const finalContent = streamingContentRef.current
              streamingContentRef.current = ''
              setMessages(prev => {
                const withoutStreaming = prev.filter(m => m.id !== 'streaming')
                return [...withoutStreaming, {
                  id: `msg-${Date.now()}`,
                  role: 'assistant',
                  content: finalContent,
                  created_at: new Date().toISOString()
                }]
              })
            } else {
              streamingContentRef.current = ''
            }
          } else if (data.type === 'error') {
            setStreaming(false)
            if (data.draft_html) {
              setDraftHtml(data.draft_html)
            }
            const streamedContent = streamingContentRef.current
            streamingContentRef.current = ''
            setMessages(prev => {
              const withoutStreaming = prev.filter(m => m.id !== 'streaming')
              const msgs = streamedContent
                ? [...withoutStreaming, { id: `msg-${Date.now()}`, role: 'assistant', content: streamedContent, created_at: new Date().toISOString() }]
                : withoutStreaming
              return [...msgs, {
                id: `err-${Date.now()}`,
                role: 'assistant',
                content: `Error: ${data.message || 'Something went wrong'}`,
                created_at: new Date().toISOString()
              }]
            })
          } else if (data.type === 'ask_user' && data.question) {
            setMessages(prev => {
              const withoutStreaming = prev.filter(m => m.id !== 'streaming')
              return [...withoutStreaming, {
                id: `ask-${Date.now()}`,
                role: 'assistant',
                content: data.question!.question,
                created_at: new Date().toISOString(),
                message_type: 'ask_user',
                metadata: data.question
              }]
            })
          } else if (data.type === 'published') {
            setPublishing(false)
            setStatus('published')
            if (data.url) setPublishedUrl(data.url)
          } else if (data.type === 'publish_error') {
            setPublishing(false)
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [session.id])

  const handleSend = useCallback((content: string) => {
    const optimisticMsg: Message = {
      id: `opt-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString()
    }
    setMessages(prev => [...prev, optimisticMsg])
    setStreaming(true)

    fetch(`/landing_pages/${session.id}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ message: { content } })
    }).then(res => {
      if (res && !res.ok) setStreaming(false)
    }).catch(() => {
      setStreaming(false)
    })
  }, [session.id])

  const handlePublish = useCallback(() => {
    if (!confirm('Publish this demo? It will be publicly accessible.')) return
    setPublishing(true)
    fetch(`/landing_pages/${session.id}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      }
    }).catch(() => setPublishing(false))
  }, [session.id])

  const handleAnswer = useCallback((answer: string) => {
    // Mark the ask_user message as answered
    setMessages(prev => prev.map(m =>
      m.message_type === 'ask_user' && !m.answered ? { ...m, answered: answer } : m
    ))

    fetch(`/landing_pages/${session.id}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ answer })
    })
  }, [session.id])

  return (
    <Layout fullWidth>
      <div className="flex h-full flex-col">
        {/* Top Bar */}
        <div className="flex min-h-[52px] items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => router.visit('/landing_pages')}
              className="p-1 text-gray-400 transition-colors hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-gray-400">Demos</span>
            <span className="text-gray-300">/</span>
            <span className="text-sm font-medium text-gray-900">{session.name}</span>
            {status === 'published' ? (
              <span className="inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                Published
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-600">
                <Pencil className="h-2.5 w-2.5" />
                Draft
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {publishedUrl && (
              <a
                href={publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                View Live
              </a>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm('Save this demo as a reusable template?')) {
                  fetch(`/landing_pages/${session.id}/save_as_template`, {
                    method: 'POST',
                    headers: {
                      'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
                    },
                  }).then(() => router.visit('/landing_pages'))
                }
              }}
              disabled={!draftHtml}
            >
              <Copy className="mr-1 h-3 w-3" />
              Save as Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm(`Delete "${session.name}"? This cannot be undone.`)) {
                  router.delete(`/landing_pages/${session.id}`)
                }
              }}
              className="text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </Button>
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publishing || !draftHtml}
            >
              {publishing ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Upload className="mr-1 h-3 w-3" />
              )}
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          </div>
        </div>

        {/* Split Panel */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat */}
          <div className="flex w-[45%] min-w-[380px] flex-col border-r border-gray-200">
            <ChatPanel messages={messages} streaming={streaming} onAnswer={handleAnswer} />
            <ChatInput onSend={handleSend} disabled={streaming} />
          </div>

          {/* Preview */}
          <PreviewPanel html={draftHtml} />
        </div>
      </div>
    </Layout>
  )
}
